<template>
  <div class="customer-referral-v2">
    <button type="button" class="customer-referral-v2__card" @click="openModal">
      <!--<div class="customer-referral-v2__left">
        <IconEmbedded class="customer-referral-v2__gift-icon" name="gift_2" :size="22" />
      </div>-->
      <div class="customer-referral-v2__middle">
        <div class="customer-referral-v2__title">Get a month free!</div>
        <div class="customer-referral-v2__subtitle">Refer a destination</div>
      </div>
      <div class="customer-referral-v2__right">
        <IconEmbedded name="caret-right_2-5" :size="17" />
      </div>
    </button>

    <SrpModal v-model:isVisible="isModalVisible" size="medium" :isClosable="!isSubmitting">
      <template #header>
        <h2 class="global-h2">{{ isSubmitted ? "Thanks for the referral!" : "Give a Month, Get a Month" }}</h2>
      </template>

      <template #content>
        <div v-if="!isSubmitted">
          <p class="global-p customer-referral-v2__intro">
            Know a destination that should be on Shrpa? Send us their info. <br />
            Once we connect with them over a demo, you get a free month added to your account.<br />If they sign up, they get one too.
          </p>

          <div class="customer-referral-v2__field-wrap">
            <label class="global-p customer-referral-v2__label">Organization *</label>
            <input v-model="form.organization" class="global-text-input" type="text" maxlength="140" />
          </div>

          <div class="customer-referral-v2__row">
            <div class="customer-referral-v2__field-wrap">
              <label class="global-p customer-referral-v2__label">First name *</label>
              <input v-model="form.firstName" class="global-text-input" type="text" maxlength="80" />
            </div>
            <div class="customer-referral-v2__field-wrap">
              <label class="global-p customer-referral-v2__label">Last name *</label>
              <input v-model="form.lastName" class="global-text-input" type="text" maxlength="80" />
            </div>
          </div>

          <div class="customer-referral-v2__field-wrap">
            <label class="global-p customer-referral-v2__label">Email address *</label>
            <input v-model="form.emailAddress" class="global-text-input" type="email" maxlength="180" />
          </div>

          <div class="customer-referral-v2__field-wrap">
            <label class="global-p customer-referral-v2__label">Phone number</label>
            <input v-model="form.phoneNumber" class="global-text-input" type="tel" maxlength="40" />
          </div>

          <div v-if="submitError" class="customer-referral-v2__error">{{ submitError }}</div>
        </div>

        <div v-else>
          <p class="global-p">We will reach out to them and credit your account as soon as the demo is set.</p>
        </div>
      </template>

      <template #footer>
        <SrpButton color="gray" fill="outlined" style="margin-right: 10px" :isDisabled="isSubmitting" @click="closeModal">
          {{ isSubmitted ? "Close" : "Cancel" }}
        </SrpButton>
        <SrpButton v-if="!isSubmitted" :isDisabled="!isFormValid || isSubmitting" @click="submitReferral">
          <template #icon>
            <LoadingIcon v-if="isSubmitting" :size="20" />
            <IconEmbedded v-else name="send_2-5" :size="20" color="rgba(255, 255, 255, 0.5)" />
          </template>
          Send referral
        </SrpButton>
      </template>
    </SrpModal>
  </div>
</template>

<script lang="ts">
let referralDeepLinkHandled = false;
</script>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { shrpaApi } from "@repos/axios";
import { emailValidationRegExps } from "@helpers/EmailHelper";
import { RouteHelper } from "@helpers/RouteHelper";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

const props = defineProps<{
  customerId: string;
}>();

const route = useRoute();
const router = useRouter();

type ReferralPayload = {
  organization: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string | null;
};

const isModalVisible = ref(false);
const isSubmitting = ref(false);
const isSubmitted = ref(false);
const submitError = ref("");

const form = reactive({
  organization: "",
  firstName: "",
  lastName: "",
  emailAddress: "",
  phoneNumber: "",
});

const isFormValid = computed(() => {
  return form.organization.trim().length > 0 && form.firstName.trim().length > 0 && form.lastName.trim().length > 0 && emailValidationRegExps.forgiving.test(form.emailAddress.trim());
});

function openModal() {
  isModalVisible.value = true;
}

onMounted(() => {
  if (referralDeepLinkHandled) return;
  const redirect = RouteHelper.getQueryStringParam("r");
  if (redirect?.toUpperCase() !== "REFERRAL") return;

  referralDeepLinkHandled = true;
  openModal();

  const query = { ...route.query };
  for (const key of Object.keys(query)) {
    if (key.toLowerCase() === "r") delete query[key];
  }
  router.replace({ path: route.path, query, hash: route.hash });
});

function resetForm() {
  form.organization = "";
  form.firstName = "";
  form.lastName = "";
  form.emailAddress = "";
  form.phoneNumber = "";
  submitError.value = "";
  isSubmitting.value = false;
  isSubmitted.value = false;
}

function closeModal() {
  isModalVisible.value = false;
  if (!isSubmitting.value) {
    resetForm();
  }
}

async function submitReferral() {
  if (!isFormValid.value || isSubmitting.value) {
    return;
  }
  isSubmitting.value = true;
  submitError.value = "";

  const payload: ReferralPayload = {
    organization: form.organization.trim(),
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    emailAddress: form.emailAddress.trim(),
    phoneNumber: form.phoneNumber.trim() || null,
  };

  try {
    await shrpaApi.post(`/pages/${encodeURIComponent(props.customerId)}/referral/v2`, payload);
    isSubmitted.value = true;
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped lang="scss">
.customer-referral-v2 {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  &__card {
    width: 100%;
    min-height: 64px;
    padding: 10px 12px;
    border: 2px solid lightgray;
    border-radius: 12px;
    display: flex;
    align-items: center;
    background: #fafafa;
    cursor: pointer;
    transition: border-color 0.1s ease-in-out;
    text-align: left;
  }

  &__card:hover {
    border-color: rgba(62, 189, 184, 0.8);
  }

  &__left {
    margin-right: 10px;
    color: rgba(17, 151, 146, 1);
  }

  &__gift-icon {
    opacity: 0.9;
  }

  &__middle {
    flex: 1;
  }

  &__right {
    color: rgba(17, 151, 146, 0.85);
  }

  &__title {
    color: rgba(17, 151, 146, 1);
    font-size: 17px;
    font-weight: 700;
    line-height: 1.2;
  }

  &__subtitle {
    margin-top: 1px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 13px;
    line-height: 1.2;
  }

  &__intro {
    margin-bottom: 14px;
  }

  &__field-wrap {
    margin-bottom: 11px;
  }

  &__label {
    margin-bottom: 4px;
  }

  &__row {
    gap: 10px;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  &__error {
    margin-top: 8px;
    color: rgba(192, 32, 32, 1);
    font-size: 14px;
  }
}

@media screen and (max-width: 700px) {
  .customer-referral-v2 {
    &__title {
      font-size: 17px;
    }

    &__subtitle {
      font-size: 13px;
    }

    &__row {
      grid-template-columns: 1fr;
    }
  }
}
</style>
